/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 03-06-2026
 *
 * Cytoscape stylesheet voor de modelbouw-canvas. Vervangt de AngularJS-constante
 * `cytoModelStyle` uit `app/cytostyles/modelcytostyle.js`. Wordt door
 * `DlModelCanvasComponent` meegegeven aan Cytoscape bij init.
 */
import type { StylesheetJson } from 'cytoscape';

const feedbackhighlight = '#ff5900';

// De Cytoscape-typings voor Css.Core/Node/Edge zijn streng en eisen properties die
// runtime niet vereist zijn; daarom typeren we de array als StylesheetJson via cast.
export const cytoModelStyle: StylesheetJson = [
    // core
    { selector: 'core', css: { 'outside-texture-bg-color': '#fff' } },

    // algemene node-styling
    {
        selector: 'node.model',
        css: {
            'text-events': 'yes',
            'shape': 'ellipse',
            'text-halign': 'right',
            'text-valign': 'center',
            'text-margin-x': 3,
            'font-size': 25,
            'background-color': 'white',
            'background-opacity': 0,
            'background-fit': 'contain',
            'background-repeat': 'no-repeat',
            'color': 'black'
        }
    },
    {
        selector: 'node.simulation',
        css: {
            'text-events': 'yes',
            'shape': 'ellipse',
            'text-halign': 'right',
            'text-valign': 'center',
            'text-margin-x': 3,
            'font-size': 25,
            'background-color': 'white',
            'background-opacity': 0,
            'background-fit': 'contain',
            'background-repeat': 'no-repeat',
            'color': 'black'
        }
    },

    { selector: 'node[label]', css: { 'label': 'data(label)' } },
    { selector: 'node.nolabel', css: { 'text-opacity': 0 } },
    { selector: '.simulation', css: { 'z-index': 10 } },

    { selector: 'node.model', css: { 'width': 50, 'height': 50 } },
    { selector: 'node.simulation', css: { 'width': 50, 'height': 50 } },

    { selector: 'node[width]', css: { 'width': 'data(width)' } },
    { selector: 'node[height]', css: { 'height': 'data(height)' } },

    { selector: 'node:selected', css: {} },
    { selector: '.hidden', css: { 'display': 'none' } },
    { selector: '.hide_in_simulate[!condtag].simulating', css: { 'display': 'none' } },

    // basistypen met iconen
    { selector: '.entity', css: { 'background-image': 'resources/img/entity.svg' } },
    { selector: '.quantity', css: { 'background-image': 'resources/img/quantity.svg' } },

    {
        selector: '.attribute',
        css: {
            'background-image': 'resources/img/attribute.svg',
            'shape': 'rectangle',
            'width': 75,
            'height': 25
        }
    },

    { selector: '.agent', css: { 'background-image': 'resources/img/agent.svg' } },

    {
        selector: '.configuration',
        css: {
            'shape': 'rectangle',
            'height': 20,
            'width': 50,
            'text-valign': 'center',
            'text-halign': 'center'
        }
    },

    { selector: '.assumption', css: { 'background-image': 'resources/img/assumption.svg', 'shape': 'diamond' } },

    // inequalities basis
    { selector: '.q_ineq_lt', css: { 'background-image': 'resources/img/inequality_smaller.svg' } },
    { selector: '.d_ineq_lt', css: { 'background-image': 'resources/img/inequality_smaller.svg' } },
    { selector: '.q_ineq_lte', css: { 'background-image': 'resources/img/inequality_equal_or_smaller.svg' } },
    { selector: '.d_ineq_lte', css: { 'background-image': 'resources/img/inequality_equal_or_smaller.svg' } },
    { selector: '.q_ineq_eq', css: { 'background-image': 'resources/img/inequality_equal.svg' } },
    { selector: '.d_ineq_eq', css: { 'background-image': 'resources/img/inequality_equal.svg' } },
    { selector: '.q_ineq_gte', css: { 'background-image': 'resources/img/inequality_equal_or_greater.svg' } },
    { selector: '.d_ineq_gte', css: { 'background-image': 'resources/img/inequality_equal_or_greater.svg' } },
    { selector: '.q_ineq_gt', css: { 'background-image': 'resources/img/inequality_greater.svg' } },
    { selector: '.d_ineq_gt', css: { 'background-image': 'resources/img/inequality_greater.svg' } },

    // inequalities condstate = "cond"
    { selector: '.q_ineq_lt[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_smaller_cond.svg' } },
    { selector: '.d_ineq_lt[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_smaller_cond.svg' } },
    { selector: '.q_ineq_lte[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_equal_or_smaller_cond.svg' } },
    { selector: '.d_ineq_lte[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_equal_or_smaller_cond.svg' } },
    { selector: '.q_ineq_eq[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_equal_cond.svg' } },
    { selector: '.d_ineq_eq[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_equal_cond.svg' } },
    { selector: '.q_ineq_gte[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_equal_or_greater_cond.svg' } },
    { selector: '.d_ineq_gte[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_equal_or_greater_cond.svg' } },
    { selector: '.q_ineq_gt[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_greater_cond.svg' } },
    { selector: '.d_ineq_gt[condstate = "cond"]', css: { 'background-image': 'resources/img/inequality_greater_cond.svg' } },

    // inequalities condstate = "cons"
    { selector: '.q_ineq_lt[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_smaller_cons.svg' } },
    { selector: '.d_ineq_lt[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_smaller_cons.svg' } },
    { selector: '.q_ineq_lte[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_equal_or_smaller_cons.svg' } },
    { selector: '.d_ineq_lte[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_equal_or_smaller_cons.svg' } },
    { selector: '.q_ineq_eq[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_equal_cons.svg' } },
    { selector: '.d_ineq_eq[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_equal_cons.svg' } },
    { selector: '.q_ineq_gte[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_equal_or_greater_cons.svg' } },
    { selector: '.d_ineq_gte[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_equal_or_greater_cons.svg' } },
    { selector: '.q_ineq_gt[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_greater_cons.svg' } },
    { selector: '.d_ineq_gt[condstate = "cons"]', css: { 'background-image': 'resources/img/inequality_greater_cons.svg' } },

    // inequalities simulation varianten
    { selector: '.q_ineq_lt.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_smaller.svg' } },
    { selector: '.d_ineq_lt.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_smaller.svg' } },
    { selector: ".q_ineq_lt.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_smaller.svg' } },
    { selector: ".d_ineq_lt.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_smaller.svg' } },
    { selector: ".q_ineq_lt.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_smaller.svg' } },
    { selector: ".d_ineq_lt.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_smaller.svg' } },

    { selector: '.q_ineq_lte.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_equal_or_smaller.svg' } },
    { selector: '.d_ineq_lte.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_equal_or_smaller.svg' } },
    { selector: ".q_ineq_lte.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_equal_or_smaller.svg' } },
    { selector: ".d_ineq_lte.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_equal_or_smaller.svg' } },
    { selector: ".q_ineq_lte.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_equal_or_smaller.svg' } },
    { selector: ".d_ineq_lte.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_equal_or_smaller.svg' } },

    { selector: '.q_ineq_eq.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_equal.svg' } },
    { selector: '.d_ineq_eq.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_equal.svg' } },
    { selector: ".q_ineq_eq.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_equal.svg' } },
    { selector: ".d_ineq_eq.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_equal.svg' } },
    { selector: ".q_ineq_eq.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_equal.svg' } },
    { selector: ".d_ineq_eq.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_equal.svg' } },

    { selector: '.q_ineq_gte.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_equal_or_greater.svg' } },
    { selector: '.d_ineq_gte.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_equal_or_greater.svg' } },
    { selector: ".q_ineq_gte.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_equal_or_greater.svg' } },
    { selector: ".d_ineq_gte.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_equal_or_greater.svg' } },
    { selector: ".q_ineq_gte.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_equal_or_greater.svg' } },
    { selector: ".d_ineq_gte.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_equal_or_greater.svg' } },

    { selector: '.q_ineq_gt.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_greater.svg' } },
    { selector: '.d_ineq_gt.simulation', css: { 'background-image': 'resources/img/simnodes/green/inequality_greater.svg' } },
    { selector: ".q_ineq_gt.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_greater.svg' } },
    { selector: ".d_ineq_gt.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/inequality_greater.svg' } },
    { selector: ".q_ineq_gt.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_greater.svg' } },
    { selector: ".d_ineq_gt.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/inequality_greater.svg' } },

    // calculi
    { selector: '.q_calc_plus', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_addition.svg' } },
    { selector: '.d_calc_plus', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_addition.svg' } },
    { selector: '.q_calc_min', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_subtraction.svg' } },
    { selector: '.d_calc_min', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_subtraction.svg' } },
    { selector: '.q_calc_mult', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_multiplication.svg' } },
    { selector: '.d_calc_mult', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_multiplication.svg' } },
    { selector: '.q_calc_div', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_division.svg' } },
    { selector: '.d_calc_div', css: { 'shape': 'rectangle', 'width': 75, 'height': 25, 'background-image': 'resources/img/calc_division.svg' } },

    // correspondences
    { selector: '.correspondence_qs_normal', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_qs_directed', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_dqs_normal', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_dqs_directed', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_qv_normal', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_qv_directed', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_dv_normal', css: { 'background-image': 'resources/img/correspondentie.svg' } },
    { selector: '.correspondence_dv_directed', css: { 'background-image': 'resources/img/correspondentie.svg' } },

    { selector: '.correspondence_qs_reverse', css: { 'background-image': 'resources/img/correspondentie_inverse.svg' } },
    { selector: '.correspondence_qs_directed_reverse', css: { 'background-image': 'resources/img/correspondentie_inverse.svg' } },
    { selector: '.correspondence_dqs_reverse', css: { 'background-image': 'resources/img/correspondentie_inverse.svg' } },
    { selector: '.correspondence_dqs_directed_reverse', css: { 'background-image': 'resources/img/correspondentie_inverse.svg' } },

    { selector: '.correspondence_qs_normal[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_qs_directed[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_dqs_normal[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_dqs_directed[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_qv_normal[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_qv_directed[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_dv_normal[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },
    { selector: '.correspondence_dv_directed[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_cons.svg' } },

    { selector: '.correspondence_qs_reverse[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_inverse_cons.svg' } },
    { selector: '.correspondence_qs_directed_reverse[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_inverse_cons.svg' } },
    { selector: '.correspondence_dqs_reverse[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_inverse_cons.svg' } },
    { selector: '.correspondence_dqs_directed_reverse[condstate = "cons"]', css: { 'background-image': 'resources/img/correspondentie_inverse_cons.svg' } },

    // influences
    { selector: '.influence_positive', css: { 'background-image': 'resources/img/influence_positive.svg' } },
    { selector: '.influence_positive[condstate = "cons"]', css: { 'background-image': 'resources/img/influence_positive_cons.svg' } },
    { selector: '.influence_negative', css: { 'background-image': 'resources/img/influence_negative.svg' } },
    { selector: '.influence_negative[condstate = "cons"]', css: { 'background-image': 'resources/img/influence_negative_cons.svg' } },

    // proportionality
    { selector: '.proportionality_negative', css: { 'background-image': 'resources/img/proportionality_negative.svg' } },
    { selector: '.proportionality_negative[condstate = "cons"]', css: { 'background-image': 'resources/img/proportionality_negative_cons.svg' } },
    { selector: '.proportionality_positive', css: { 'background-image': 'resources/img/proportionality_positive.svg' } },
    { selector: '.proportionality_positive[condstate = "cons"]', css: { 'background-image': 'resources/img/proportionality_positive_cons.svg' } },

    { selector: '.proportionality_positive[mln < 40]', css: { 'background-image': 'resources/img/causal_plus.svg' } },
    { selector: '.proportionality_negative[mln < 40]', css: { 'background-image': 'resources/img/causal_min.svg' } },

    { selector: '.proportionality_multiply', css: { 'background-image': 'resources/img/proportionality_multiply.svg' } },
    { selector: '.proportionality_multiply[condstate = "cons"]', css: { 'background-image': 'resources/img/proportionality_multiply_cons.svg' } },
    { selector: '.proportionality_divide', css: { 'background-image': 'resources/img/proportionality_divide.svg' } },
    { selector: '.proportionality_divide[condstate = "cons"]', css: { 'background-image': 'resources/img/proportionality_divide_cons.svg' } },

    // derivative en quantity space
    { selector: '.derivative', css: { 'background-image': 'resources/img/derivative_sign.svg', 'shape': 'square', 'width': 30, 'height': 35 } },

    {
        selector: '.quantity_space',
        css: {
            'background-image': 'resources/img/qspace.svg',
            'shape': 'square',
            'background-fit': 'contain',
            'width': 30,
            'height': 40,
            'background-repeat': 'no-repeat'
        }
    },
    { selector: '.quantity_space_point', css: { 'background-image': 'resources/img/quantity_space_point.svg', 'width': 20, 'height': 30 } },
    { selector: '.quantity_space_point[?isZero]', css: { 'background-image': 'resources/img/derivative_qs_steady.svg', 'shape': 'square' } },
    { selector: '.quantity_space_interval', css: { 'background-image': 'resources/img/quantity_space_interval.svg', 'shape': 'square', 'width': 20, 'height': 30 } },

    // values
    { selector: '.quantity_value, .derivative_value', css: { 'background-image': 'resources/img/value.svg', 'shape': 'square', 'width': 40, 'height': 50 } },
    { selector: '.quantity_value[condstate = "cond"]', css: { 'background-image': 'resources/img/value_cond.svg' } },
    { selector: '.derivative_value[condstate = "cond"]', css: { 'background-image': 'resources/img/value_cond.svg' } },
    { selector: '.quantity_value[condstate = "cons"]', css: { 'background-image': 'resources/img/value_cons.svg' } },
    { selector: '.derivative_value[condstate = "cons"]', css: { 'background-image': 'resources/img/value_cons.svg' } },

    { selector: '.quantity_value.simulation', css: { 'background-image': 'resources/img/simnodes/green/value.svg' } },
    { selector: '.derivative_value.simulation', css: { 'background-image': 'resources/img/simnodes/green/value.svg' } },
    { selector: ".quantity_value.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/value.svg' } },
    { selector: ".derivative_value.simulation[simtype='terminatie']", css: { 'background-image': 'resources/img/simnodes/orange/value.svg' } },
    { selector: ".quantity_value.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/value.svg' } },
    { selector: ".derivative_value.simulation[simtype='ordering']", css: { 'background-image': 'resources/img/simnodes/brown/value.svg' } },

    // derivative markeringen
    { selector: '.derivative_plus', css: { 'background-image': 'resources/img/derivative_qs_increase.svg', 'shape': 'square', 'width': 20, 'height': 30 } },
    { selector: '.derivative_zero', css: { 'background-image': 'resources/img/derivative_qs_steady.svg', 'shape': 'square', 'width': 20, 'height': 30 } },
    { selector: '.derivative_min', css: { 'background-image': 'resources/img/derivative_qs_decrease.svg', 'shape': 'square', 'width': 20, 'height': 30 } },

    { selector: '.node_highlight', css: {} },

    // exogenen
    { selector: '.isa_q_exo', css: { 'shape': 'square', 'width': 45, 'height': 45, 'background-repeat': 'no-repeat', 'background-fit': 'contain' } },
    { selector: '.q_exo_decr', css: { 'background-image': 'resources/img/exogeen_dalen.svg' } },
    { selector: '.q_exo_steady', css: { 'background-image': 'resources/img/exogeen_constant.svg' } },
    { selector: '.q_exo_incr', css: { 'background-image': 'resources/img/exogeen_stijgen.svg' } },
    { selector: '.q_exo_sin', css: { 'background-image': 'resources/img/exogeen_sinus.svg' } },
    { selector: '.q_exo_para_pos', css: { 'background-image': 'resources/img/exogeen_parabool_positief.svg' } },
    { selector: '.q_exo_para_neg', css: { 'background-image': 'resources/img/exogeen_parabool_negatief.svg' } },
    { selector: '.q_exo_random', css: { 'background-image': 'resources/img/exogeen_random.svg' } },

    { selector: '.quantity_allvalues', css: { 'shape': 'square', 'width': 45, 'height': 45, 'background-repeat': 'no-repeat', 'background-fit': 'contain', 'background-image': 'resources/img/exogeen_alle_waarden.svg' } },

    // conditiesets z-index + zichtbaarheid
    { selector: '.model[condtag]', css: { 'z-index': 7 } },
    { selector: '.model.uitconditieset[?condtag]', css: { 'display': 'none' } },
    { selector: '.model.uitconditiestate', css: { 'background-image-opacity': 0.3, 'text-opacity': 0.5 } },
    { selector: '.model.uitconditiestate.inconditieset', css: { 'background-image-opacity': 0.4, 'text-opacity': 0.5 } },
    { selector: '.model.inconditieset.inconditiestate', css: { 'z-index': 8 } },
    // een zichtbare conditiewaarde buiten de actieve stand: de kleur van 40% dekking op wit in plaats van
    // doorzichtigheid, anders lijkt de overlap met een opzij geschoven waarde een extra pijl
    {
        selector: '.quantity_value[condstate = "cond"].uitconditiestate.inconditieset, .derivative_value[condstate = "cond"].uitconditiestate.inconditieset',
        css: { 'background-image': 'resources/img/value_cond_licht.svg', 'background-image-opacity': 1 }
    },
    {
        selector: '.quantity_value[condstate = "cons"].uitconditiestate.inconditieset, .derivative_value[condstate = "cons"].uitconditiestate.inconditieset',
        css: { 'background-image': 'resources/img/value_cons_licht.svg', 'background-image-opacity': 1 }
    },

    // waarden bij hetzelfde element schuiven opzij zodat ze allemaal zichtbaar zijn (DlModel.checkValuenodesZichtbaar). Het
    // plaatje vult de hele node, dus het schuift er deels uit: niet afknippen, en de bounding box mee vergroten. Wie het
    // verst opzij staat ligt bovenop (gewoon, dan conditie, dan consequentie), zodat elke pijl de staart van de
    // pijl rechts ervan bedekt en van elke waarde de punt te zien is
    { selector: '.model.opzij1, .model.opzij2', css: { 'background-clip': 'none', 'bounds-expansion': 40 } },
    { selector: '.model.opzij1', css: { 'background-position-x': -20 } },
    { selector: '.model.opzij2', css: { 'background-position-x': -40 } },
    { selector: '.model.opzij1[?condtag]', css: { 'z-index': 9 } },
    { selector: '.model.onderconditie', css: { 'background-image-opacity': 1, 'z-index': 10 } },
    // alleen het plaatje schuift opzij: het klikvlak blijft over de waarde eronder liggen. In de conditiemodus laat
    // een verschoven waarde buiten de actieve stand de klik daarom door, zodat de waarde van die stand te
    // selecteren (en te verwijderen) is
    { selector: '.model.uitconditiestate.opzij1, .model.uitconditiestate.opzij2', css: { 'events': 'no' } },

    { selector: '.node_disable', css: { 'background-image-opacity': 0.5, 'text-opacity': 0.5 } },

    // edgehandles
    { selector: '.edgeHandle', css: { 'text-events': 'no', 'height': 1, 'width': 1, 'background-color': 'transparent' } },
    { selector: '.edgeHandle[?freeMove]', css: { 'height': 10, 'width': 10, 'background-color': 'red' } },

    // Edges algemeen
    {
        selector: 'edge',
        css: {
            'curve-style': 'bezier',
            'width': 1,
            'line-color': 'grey',
            'source-arrow-color': 'grey',
            'target-arrow-color': 'grey',
            'mid-target-arrow-color': 'grey',
            'mid-source-arrow-color': 'grey',
            'arrow-scale': 1.5
        }
    },

    { selector: 'edge.model.uitconditieset', css: { 'opacity': 0.3 } },
    { selector: 'edge.model.uitconditiestate.inconditieset', css: { 'opacity': 0.5 } },
    { selector: 'edge.model.inconditiestate', css: { 'z-index': 8 } },
    { selector: 'edge.model.uitconditieset.uitconditiestate', css: { 'display': 'none' } },

    { selector: 'edge.edge_disable', css: { 'opacity': 0.5 } },

    { selector: 'edge.edge_to_isa_relation', css: { 'line-color': 'blue', 'width': 1, 'line-style': 'solid', 'source-arrow-color': 'blue', 'target-arrow-color': 'blue', 'mid-target-arrow-color': 'blue', 'mid-source-arrow-color': 'blue' } },
    { selector: 'edge.edge_to_isa_relation[condstate="cond"]', css: { 'line-color': 'red', 'source-arrow-color': 'red', 'target-arrow-color': 'red', 'mid-target-arrow-color': 'red', 'mid-source-arrow-color': 'red' } },
    { selector: 'edge.edge_to_isa_relation[condstate="cons"]', css: { 'line-color': '#FFA203', 'source-arrow-color': '#FFA203', 'target-arrow-color': '#FFA203', 'mid-target-arrow-color': '#FFA203', 'mid-source-arrow-color': '#FFA203' } },
    { selector: 'edge.edge_to_isa_relation.edge_type_source', css: {} },
    { selector: 'edge.edge_to_isa_relation.edge_type_target', css: { 'source-arrow-shape': 'triangle' } },

    { selector: 'edge.edge_to_isa_relation.simulation', css: { 'line-color': '#02C700', 'source-arrow-color': '#02C700', 'target-arrow-color': '#02C700' } },
    { selector: "edge.edge_to_isa_relation.simulation[simtype='terminatie']", css: { 'line-color': '#FF7F00', 'source-arrow-color': '#FF7F00', 'target-arrow-color': '#FF7F00' } },
    { selector: "edge.edge_to_isa_relation.simulation[simtype='ordering']", css: { 'line-color': '#964B0D', 'source-arrow-color': '#964B0D', 'target-arrow-color': '#964B0D' } },

    { selector: 'edge.edge_to_isa_q_calc', css: { 'line-color': 'black', 'width': 1, 'line-style': 'solid', 'source-arrow-color': 'black', 'target-arrow-color': 'black', 'mid-target-arrow-color': 'black', 'mid-source-arrow-color': 'black' } },
    { selector: 'edge.edge_to_isa_d_calc', css: { 'line-color': 'black', 'width': 1, 'line-style': 'solid', 'source-arrow-color': 'black', 'target-arrow-color': 'black', 'mid-target-arrow-color': 'black', 'mid-source-arrow-color': 'black' } },

    { selector: 'edge.edge_to_q_calc_plus', css: { 'source-arrow-shape': 'none' } },
    { selector: 'edge.edge_to_d_calc_plus', css: { 'source-arrow-shape': 'none' } },
    { selector: 'edge.edge_to_q_calc_mult', css: { 'source-arrow-shape': 'none' } },
    { selector: 'edge.edge_to_d_calc_mult', css: { 'source-arrow-shape': 'none' } },

    { selector: 'edge.edge_to_correspondence_qs_normal', css: { 'source-arrow-shape': 'triangle' } },
    { selector: 'edge.edge_to_correspondence_qs_reverse', css: { 'source-arrow-shape': 'triangle' } },
    { selector: 'edge.edge_to_correspondence_dqs_normal', css: { 'source-arrow-shape': 'triangle' } },
    { selector: 'edge.edge_to_correspondence_dqs_reverse', css: { 'source-arrow-shape': 'triangle' } },
    { selector: 'edge.edge_to_correspondence_qv_normal', css: { 'source-arrow-shape': 'triangle' } },
    { selector: 'edge.edge_to_correspondence_dv_normal', css: { 'source-arrow-shape': 'triangle' } },

    // simulatie history
    {
        selector: 'node.simhistory_container',
        css: {
            'shape': 'rectangle',
            'background-color': '#fff',
            'background-opacity': 1,
            'border-width': 1,
            'border-style': 'solid',
            'border-color': 'black',
            'padding-top': 6,
            'padding-left': 6,
            'padding-bottom': 6,
            'padding-right': 6,
            'z-index': 15
        }
    },
    { selector: 'edge.edge_to_simhistory_container', css: { 'line-color': 'green' } },
    { selector: 'edge.edge_to_simhistory_container.edge_type_target', css: { 'source-arrow-shape': 'triangle' } },

    { selector: 'node.simhistory', css: { 'background-fit': 'contain', 'background-clip': 'none', 'background-repeat': 'no-repeat', 'z-index': 20 } },
    { selector: 'node.simhistory_valuelabel', css: { 'label': 'data(label)', 'font-size': 12, 'text-valign': 'center', 'text-halign': 'right', 'shape': 'polygon', 'width': 1, 'height': 1, 'z-index': 24, 'color': 'red' } },
    { selector: 'node.simhistory_background', css: { 'shape': 'rectangle', 'background-color': 'lightgrey', 'background-opacity': 1, 'z-index': 22 } },
    { selector: 'node.simhistory_pointblock', css: { 'shape': 'rectangle', 'background-color': 'white', 'height': 4, 'background-opacity': 1, 'z-index': 23 } },

    { selector: 'node.simhistory_statenumber', css: { 'label': 'data(num)', 'font-size': 12, 'text-valign': 'center', 'text-halign': 'center', 'shape': 'rectangle', 'z-index': 24 } },
    { selector: 'node.simhistory_valuemarker', css: { 'shape': 'ellipse', 'background-opacity': 0, 'background-fit': 'contain', 'background-repeat': 'no-repeat', 'width': 20, 'height': 20, 'z-index': 24 } },
    { selector: 'node.simhistory_ineqmarker', css: { 'shape': 'ellipse', 'background-opacity': 0, 'background-fit': 'contain', 'background-repeat': 'no-repeat', 'width': 20, 'height': 20, 'z-index': 24 } },
    { selector: 'node.simhistory_dvaluemarker', css: { 'shape': 'ellipse', 'background-opacity': 0, 'background-fit': 'contain', 'background-repeat': 'no-repeat', 'width': 10, 'height': 10, 'z-index': 24 } },

    { selector: 'node.simhistory_valuemarker_plus, node.simhistory_dvaluemarker_pos', css: { 'background-image': 'resources/img/simnodes/valuehistory_up_200px.svg' } },
    { selector: 'node.simhistory_valuemarker_zero, node.simhistory_dvaluemarker_zero', css: { 'background-image': 'resources/img/simnodes/valuehistory_equal_200px.svg' } },
    { selector: 'node.simhistory_valuemarker_min, node.simhistory_dvaluemarker_neg', css: { 'background-image': 'resources/img/simnodes/valuehistory_down_200px.svg' } },

    { selector: '.simhistory_ineqmarker_q_ineq_lt.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_smaller.svg' } },
    { selector: '.simhistory_ineqmarker_d_ineq_lt.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_smaller.svg' } },
    { selector: '.simhistory_ineqmarker_q_ineq_lte.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_equal_or_smaller.svg' } },
    { selector: '.simhistory_ineqmarker_d_ineq_lte.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_equal_or_smaller.svg' } },
    { selector: '.simhistory_ineqmarker_q_ineq_eq.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_equal.svg' } },
    { selector: '.simhistory_ineqmarker_d_ineq_eq.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_equal.svg' } },
    { selector: '.simhistory_ineqmarker_q_ineq_gte.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_equal_or_greater.svg' } },
    { selector: '.simhistory_ineqmarker_d_ineq_gte.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_equal_or_greater.svg' } },
    { selector: '.simhistory_ineqmarker_q_ineq_gt.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_greater.svg' } },
    { selector: '.simhistory_ineqmarker_d_ineq_gt.simulation', css: { 'background-image': 'resources/img/simnodes/white/inequality_greater.svg' } },

    { selector: 'node.simhistory_valuemarker_unknown, node.simhistory_ineqmarker_unknown', css: { 'font-size': 15, 'text-halign': 'center', 'text-valign': 'center', 'background-opacity': 1, 'background-color': 'white', 'label': '?' } },

    // node-dom-highlight (minder sterk): een node-overlay die zichzelf aanwijst, bijvoorbeeld een
    // open suggestieballon. Staat bewust vóór de feedback-highlight hieronder: beide zetten dezelfde
    // eigenschappen, en bij gelijke specificiteit wint in Cytoscape de laatste regel. Andersom bleef
    // een node met een open ballon op 2px staan als de feedbackbalk hem aanwees, waardoor het
    // aanklikken van een genummerd cirkeltje geen zichtbaar effect had.
    { selector: 'node.node-dom-highlight', css: { 'border-color': feedbackhighlight, 'border-width': 2 } },
    {
        selector: '.configuration.node-dom-highlight',
        css: {
            'border-width': 0,
            'text-background-color': '#ffffff',
            'text-background-opacity': 0,
            'text-background-shape': 'round-rectangle',
            'text-background-padding': 4,
            'text-border-width': 2,
            'text-border-style': 'solid',
            'text-border-color': feedbackhighlight,
            'text-border-opacity': 1
        }
    },
    { selector: 'edge.node-dom-highlight', css: { 'line-color': feedbackhighlight, 'width': 1 } },

    // feedback highlight (sterker): de gebruiker wijst een element aan vanuit de feedbackbalk
    { selector: 'node.highlighted', css: { 'border-color': feedbackhighlight, 'border-width': 4 } },
    {
        selector: '.configuration.highlighted',
        css: {
            'border-width': 0,
            'text-background-color': '#ffffff',
            'text-background-opacity': 0,
            'text-background-shape': 'round-rectangle',
            'text-background-padding': 4,
            'text-border-width': 4,
            'text-border-style': 'solid',
            'text-border-color': feedbackhighlight,
            'text-border-opacity': 1
        }
    },
    { selector: 'edge.highlighted', css: { 'line-color': feedbackhighlight, 'width': 3 } }
] as unknown as StylesheetJson;
